import type { EngineContext } from "../../context";
import { isCFSFParticipatingState } from "../../constants/cfsfConstants";
import { XMLBuilder } from "fast-xml-parser";
import { schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Record1099NEC } from "./interface/1099-NEC-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import { v4 as uuidv4 } from "uuid";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { parseAmount } from "../../constants/validationFunctions";
import {
  buildRecipientDetail,
  directSaleAboveThreshold,
  resolveIrsIntakeTaxYear,
} from "./recipient-detail";
import { resolveNonemployeeCompensationAmt } from "./draft-v2-deferred";

// Valid US state codes
const VALID_US_STATES = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "MP",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "VI",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "AA",
  "AE",
  "AP",
];

/**
 * Helper function to validate and get a valid state code
 */
function getValidStateCode(state: string | undefined): string | null {
  if (!state) return null;
  const stateCode = state.substring(0, 2).toUpperCase();
  return VALID_US_STATES.includes(stateCode) ? stateCode : null;
}

/**
 * Helper function to format SoftwareId to ensure first two digits match tax year
 *
 * According to IRS documentation:
 * - New Software IDs are assigned annually for each tax year
 * - Software IDs must start with the last two digits of the tax year (e.g., "25" for 2025)
 * - The Software ID assigned by IRS for 2025 should already have this format
 *
 * This function acts as a safety check to ensure the Software ID matches the tax year.
 * Note: You should use the Software ID assigned by IRS for the current tax year.
 *
 * @param softwareId - The Software ID from environment variable (should be the IRS-assigned ID for the tax year)
 * @param taxYear - The tax year (e.g., "2025")
 * @returns Formatted Software ID with first two digits matching the tax year
 */
function formatSoftwareId(
  softwareId: string | undefined,
  taxYear: string
): string {
  // Get last two digits of tax year (e.g., "25" for 2025)
  const yearSuffix = taxYear.slice(-2);

  // If no software ID provided, return default with year prefix
  // Note: This is a fallback - you should always provide the IRS-assigned Software ID
  if (!softwareId) {
    console.warn(
      `No Software ID provided. Using default. Please use the IRS-assigned Software ID for tax year ${taxYear}.`
    );
    return `${yearSuffix}00000`;
  }

  // If SoftwareId already starts with correct year, return as-is
  // This should be the case for properly assigned Software IDs
  if (softwareId.startsWith(yearSuffix)) {
    return softwareId.substring(0, 10); // Max 10 characters per schema
  }

  // If SoftwareId doesn't start with correct year, warn and fix it
  // This handles cases where an old Software ID from a previous year might be used
  console.warn(
    `Software ID "${softwareId}" does not start with "${yearSuffix}" for tax year ${taxYear}. ` +
      `Updating to match tax year. Please ensure you're using the IRS-assigned Software ID for ${taxYear}.`
  );

  // Replace first two characters with year suffix
  // Keep remaining characters (up to 10 total length)
  const remaining = softwareId.substring(2);
  return `${yearSuffix}${remaining}`.substring(0, 10);
}

export async function generate1099NECXml(
  ctx: EngineContext,records: Record1099NEC[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  // Initialize the XML builder.
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Check if combined federal state program is enabled for this site
  // Get siteId from the first record (all records in a batch should have the same site)
  const firstRecord = records[0];
  // EXTRACTION NOTE: the CFSF election used to be two database reads
  // (customerFiles -> site id, then siteYear.combinedFederal_stateProgram).
  // The engine has no database; the host answers through ctx.lookups.cfsfEnabled.
  const isCombinedFederalEnabled = await ctx.lookups.cfsfEnabled(
    firstRecord as unknown as Record<string, unknown>
  );

  // Build an array of form details, one for each record.
  const form1099NECDetails = records.map((record) => {
    const validStateCode = getValidStateCode(record.RECIPIENT_STATE);

    return {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear,
      RecordId: record.id.toString(),
      ...(isCombinedFederalEnabled &&
        validStateCode &&
        isCFSFParticipatingState(validStateCode, taxYear) && {
          // CFSFElectionStateCd (optional, can repeat up to 62 times per schema)
          // Only include if combined federal state program is enabled AND state is in IRS-provided participating list
          // Uses 2-letter state abbreviation (e.g., "OH", "PA", "MN", "RI") per XML schema requirements
          CFSFElectionStateCd: validStateCode,
        }),
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
      RecipientDetail: buildRecipientDetail(record, {
        maskTin: transmitterData.testFileIndicator === "T",
      }),
      ...(record.ACCOUNT_NUMBER && {
        RecipientAccountNum: record.ACCOUNT_NUMBER,
      }),
      SecondTINNoticeInd: "0",
      // The IRS 2025 schema requires these elements in the exact order:
      // 1. NonemployeeCompensationAmt (optional per schema, but typically included)
      NonemployeeCompensationAmt: parseAmount(
        resolveNonemployeeCompensationAmt(record)
      ),
      // TY2026 (v1.2): boxes 1b-1d follow box 1 directly; the 2026-09-24 schema moved them
      // ahead of DirectSaleAboveThresholdInd. Emitted only when the record has a value and the
      // transmission's year supports them (a TY2025 transmission must not carry them).
      ...(Number(taxYear) >= 2026 &&
        Number(record.BOX1B_CASH_TIPS) > 0 && {
          CashTipsAmt: parseAmount(record.BOX1B_CASH_TIPS),
        }),
      ...(Number(taxYear) >= 2026 &&
        (record.BOX1C_TTOC1 || record.BOX1C_TTOC2) && {
          // maxOccurs=2 — array emits one element per code
          TreasuryTippedOccupationCd: [record.BOX1C_TTOC1, record.BOX1C_TTOC2]
            .filter((c) => c && String(c).trim() !== "")
            .map((c) => String(c).trim()),
        }),
      ...(Number(taxYear) >= 2026 &&
        Number(record.BOX1D_OVERTIME_COMPENSATION) > 0 && {
          OvertimeCompensationAmt: parseAmount(record.BOX1D_OVERTIME_COMPENSATION),
        }),
      // 2. DirectSaleAboveThresholdInd (required)
      DirectSaleAboveThresholdInd: directSaleAboveThreshold(record),
      // 3. ExcessParachutePaymentAmt (optional) - Line 3 - NEW in 2025 schema
      ...(record.BOX3_GOLDEN_PARACHUTE && {
        ExcessParachutePaymentAmt: parseAmount(record.BOX3_GOLDEN_PARACHUTE),
      }),
      // 4. FederalIncomeTaxWithheldAmt (optional per schema, but typically included)
      FederalIncomeTaxWithheldAmt: parseAmount(
        record.BOX4_FED_INCOME_TAX_WITHHELD || "0"
      ),
      // 8. StateLocalTaxGrp (optional, can occur multiple times)
      ...(shouldIncludeStateLocalTaxGrp(record) && {
        StateLocalTaxGrp: getStateLocalTaxGrp(record),
      }),
    };
  });

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure.
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1099NECDetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099NEC",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: isCombinedFederalEnabled ? "1" : "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: "2023-04-04",
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form1099NECDetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1099NECTotalAmtGrp: {
                // According to the 2025 XSD schema for Form1099NECTotalAmtGrp, proper order is:
                // 1. FederalIncomeTaxWithheldAmt (optional per schema, but typically included)
                FederalIncomeTaxWithheldAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX4_FED_INCOME_TAX_WITHHELD || "0"),
                    0
                  )
                  .toFixed(2),
                // 2. NonemployeeCompensationAmt (optional per schema, but typically included)
                NonemployeeCompensationAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + (parseFloat(resolveNonemployeeCompensationAmt(r)) || 0),
                    0
                  )
                  .toFixed(2),
                // 4-5. TY2026 (schema v5.0.6) totals, minOccurs=0 — only when any
                // record carries the amount and the schema year supports them
                ...(Number(taxYear) >= 2026 &&
                  records.some((r) => Number(r.BOX1B_CASH_TIPS) > 0) && {
                    CashTipsAmt: records
                      .reduce(
                        (sum, r) => sum + (parseFloat(r.BOX1B_CASH_TIPS || "0") || 0),
                        0
                      )
                      .toFixed(2),
                  }),
                ...(Number(taxYear) >= 2026 &&
                  records.some(
                    (r) => Number(r.BOX1D_OVERTIME_COMPENSATION) > 0
                  ) && {
                    OvertimeCompensationAmt: records
                      .reduce(
                        (sum, r) =>
                          sum +
                          (parseFloat(r.BOX1D_OVERTIME_COMPENSATION || "0") || 0),
                        0
                      )
                      .toFixed(2),
                  }),
                // ExcessParachutePaymentAmt comes after CashTipsAmt/OvertimeCompensationAmt (TY2026 v1.2)
                ...(records.some((r) => r.BOX3_GOLDEN_PARACHUTE) && {
                  ExcessParachutePaymentAmt: records
                    .reduce(
                      (sum, r) =>
                        sum + parseFloat(r.BOX3_GOLDEN_PARACHUTE || "0"),
                      0
                    )
                    .toFixed(2),
                }),
              },
              // Form1099NECTotalByStateGrp is REQUIRED when CFSFElectionStateCd is used (SHAREDIRFORM022)
              ...(isCombinedFederalEnabled && {
                Form1099NECTotalByStateGrp: calculateTotalsByState(records, taxYear),
              }),
            },
          },
          IRSubmission1Detail: {
            Form1099NECDetail: form1099NECDetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}

/**
 * Calculate totals grouped by state for CFSF submissions
 * Returns an array of Form1099NECTotalByStateGrp objects
 */
function calculateTotalsByState(
  records: Record1099NEC[],
  taxYear: string | number
): Array<{
  StateAbbreviationCd: string;
  TotalReportedRcpntFormCnt: string;
  FederalIncomeTaxWithheldAmt: string;
  NonemployeeCompensationAmt: string;
}> {
  // Group records by state
  const stateGroups = new Map<string, Record1099NEC[]>();

  records.forEach((record) => {
    const stateCode =
      record.RECIPIENT_STATE?.substring(0, 2).toUpperCase() || "XX";
    if (
      stateCode !== "XX" &&
      stateCode.length === 2 &&
      isCFSFParticipatingState(stateCode, taxYear)
    ) {
      if (!stateGroups.has(stateCode)) {
        stateGroups.set(stateCode, []);
      }
      stateGroups.get(stateCode)!.push(record);
    }
  });

  // Calculate totals for each state
  const totalsByState: Array<{
    StateAbbreviationCd: string;
    TotalReportedRcpntFormCnt: string;
    FederalIncomeTaxWithheldAmt: string;
    NonemployeeCompensationAmt: string;
  }> = [];

  stateGroups.forEach((stateRecords, stateCode) => {
    totalsByState.push({
      StateAbbreviationCd: stateCode,
      TotalReportedRcpntFormCnt: stateRecords.length.toString(),
      FederalIncomeTaxWithheldAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX4_FED_INCOME_TAX_WITHHELD || "0"),
          0
        )
        .toFixed(2),
      NonemployeeCompensationAmt: stateRecords
        .reduce(
          (sum, r) => sum + (parseFloat(resolveNonemployeeCompensationAmt(r)) || 0),
          0
        )
        .toFixed(2),
    });
  });

  return totalsByState;
}

/**
 * Helper function to determine if state/local tax groups should be included.
 */
function shouldIncludeStateLocalTaxGrp(record: Record1099NEC): boolean {
  return !!(
    (record.BOX6_PAYER_STATE_NUMBER1 &&
      (parseFloat(record.BOX5_STATE_TAX_WITHHELD1 || "0") > 0 ||
        parseFloat(record.BOX7_STATE_INCOME1 || "0") > 0)) ||
    (record.BOX6_PAYER_STATE_NUMBER2 &&
      (parseFloat(record.BOX5_STATE_TAX_WITHHELD2 || "0") > 0 ||
        parseFloat(record.BOX7_STATE_INCOME2 || "0") > 0))
  );
}

/**
 * Helper function to build state/local tax group arrays.
 */
function getStateLocalTaxGrp(record: Record1099NEC): any[] {
  const groups = [];

  if (
    record.BOX6_PAYER_STATE_NUMBER1 &&
    (parseFloat(record.BOX5_STATE_TAX_WITHHELD1 || "0") > 0 ||
      parseFloat(record.BOX7_STATE_INCOME1 || "0") > 0)
  ) {
    const stateCode = getValidStateCode(record.RECIPIENT_STATE);
    if (stateCode) {
      groups.push({
        StateAbbreviationCd: stateCode,
        StateTaxGrp: {
          StateTaxWithheldAmt: parseAmount(record.BOX5_STATE_TAX_WITHHELD1),
          StateIncomeAmt: parseAmount(record.BOX7_STATE_INCOME1),
        },
      });
    }
  }
  if (
    record.BOX6_PAYER_STATE_NUMBER2 &&
    (parseFloat(record.BOX5_STATE_TAX_WITHHELD2 || "0") > 0 ||
      parseFloat(record.BOX7_STATE_INCOME2 || "0") > 0)
  ) {
    const stateCode = getValidStateCode(record.RECIPIENT_STATE);
    if (stateCode) {
      groups.push({
        StateAbbreviationCd: stateCode,
        StateTaxGrp: {
          StateTaxWithheldAmt: parseAmount(record.BOX5_STATE_TAX_WITHHELD2),
          StateIncomeAmt: parseAmount(record.BOX7_STATE_INCOME2),
        },
      });
    }
  }
  return groups;
}
